import React from 'react';
import { Button, FormGroup, ControlLabel, FormControl, Checkbox } from 'react-bootstrap';
import { observer } from 'mobx-react';
import { FieldGroup } from './FieldGroup';
import { Validator } from './Validator';
import { required} from '../utils/ValidatorRulesUtils';
import { AlertGenerator } from './AlertGenerator';
import { ChangeEvent } from 'react';
import ConfigurationStore from '../stores/ConfigurationStore';
import OrganizationStore from '../stores/OrganizationStore';
import { IPolarisConfig } from '../models/PolarisConfig';

type PolarisHandlingType = 'None' | 'Email' | 'Account'
type NoteHandlingType = 'None' | 'Blocking' | 'NonBlocking'

type PolarisConfigProps = {
  buttonText : string,
  onSubmit : (config : any, onSuccess : () => void) => void,
  configurationStore : ConfigurationStore,
  polarisConfig : IPolarisConfig,
  closeModal? : () => void
};

type PolarisConfigState = {
    externalUrl: string,
    internalUrl: string,
    polarisUsername: string,
    polarisKey: string,
    staffDomain: string,
    staffUsername: string,
    staffPassword: string,
    logonWorkstationId: string,
    notificationHandling: boolean,
    bounceHandlingType: PolarisHandlingType,
    spamHandlingType: PolarisHandlingType,
    noteHandlingType: NoteHandlingType,
    bounceListId: string,
    spamListId: string
}

@observer
class PolarisConfigFields extends React.Component<PolarisConfigProps, PolarisConfigState> {

  static rules = [required];

  state : PolarisConfigState = {
    externalUrl: '',
    internalUrl: '',
    polarisUsername: '',
    polarisKey: '',
    staffDomain: '',
    staffUsername: '',
    staffPassword: '',
    logonWorkstationId: '',
    notificationHandling: true,
    bounceHandlingType: 'None',
    spamHandlingType: 'None',
    noteHandlingType: 'None',
    bounceListId: '',
    spamListId: ''
  };

  componentDidMount() {
    if (this.props.polarisConfig)
      this.setState({ ...this.props.polarisConfig.editableFields as PolarisConfigState})
  }

  componentDidUpdate(prevProps : PolarisConfigProps) {
    if (prevProps.polarisConfig === this.props.polarisConfig)
      return;
    
    this.setState({ ...this.state, ...this.props.polarisConfig.editableFields as PolarisConfigState});
  }

  updateField(name: any, event: ChangeEvent<EventTarget>) {
    const target = event.target as HTMLInputElement;
    let val : any = target.value;

    this.setState({
      [name]: val
    } as PolarisConfigState);
  }

  updateCheckbox(name : any, event: any) {
    this.setState({
      [name]: event.target.checked
    } as PolarisConfigState);
  }

  submit = (event : ChangeEvent<EventTarget>) => {
    event.preventDefault();

    const submitSuccess = () => {
      this.props.closeModal && this.props.closeModal();
    }
    
    this.props.onSubmit(this.state, submitSuccess);
  };

  render() {
    const { buttonText } = this.props;
    const { rules } = PolarisConfigFields;

    const { alertMessage } = this.props.configurationStore;
    const hideAlert = this.props.configurationStore.hideAlert as () => {};

    return (
      <form onSubmit={this.submit}>
        <AlertGenerator alertMessage={alertMessage} hideAlert={hideAlert}></AlertGenerator>
        <Validator rules={rules}
          fieldName='externalUrl' value={this.state.externalUrl} render={({ isValid, message }) => (
            <FieldGroup
              id='externalUrl'
              type='text'
              label='External Url'
              onChange={this.updateField.bind(this, 'externalUrl')}
              value={this.state.externalUrl}
              isValid={isValid}
              message={message}
              required
            />
          )}
        />
        <Validator rules={rules}
          fieldName='internalUrl' value={this.state.internalUrl} render={({ isValid, message }) => (
            <FieldGroup
              id='internalUrl'
              type='text'
              label='Internal Url'
              onChange={this.updateField.bind(this, 'internalUrl')}
              value={this.state.internalUrl}
              isValid={isValid}
              message={message}
              required
            />
          )}
        />
        <Validator rules={rules}
          fieldName='polarisUsername' value={this.state.polarisUsername} render={({ isValid, message }) => (
            <FieldGroup
              id='polarisUsername'
              type='text'
              label='Polaris Username'
              onChange={this.updateField.bind(this, 'polarisUsername')}
              value={this.state.polarisUsername}
              isValid={isValid}
              message={message}
              required
            />
          )}
        />
        <Validator rules={rules}
          fieldName='polarisKey' value={this.state.polarisKey} render={({ isValid, message }) => (
            <FieldGroup
              id='polarisKey'
              type='text'
              label='PolarisKey'
              onChange={this.updateField.bind(this, 'polarisKey')}
              value={this.state.polarisKey}
              isValid={isValid}
              message={message}
              required
            />
          )}
        />
        <Validator rules={rules}
          fieldName='staffDomain' value={this.state.staffDomain} render={({ isValid, message }) => (
            <FieldGroup
              id='staffDomain'
              type='text'
              label='Staff Domain'
              onChange={this.updateField.bind(this, 'staffDomain')}
              value={this.state.staffDomain}
              isValid={isValid}
              message={message}
              required
            />
          )}
        />
        <Validator rules={rules}
          fieldName='staffUsername' value={this.state.staffUsername} render={({ isValid, message }) => (
            <FieldGroup
              id='staffUsername'
              type='text'
              label='Staff Username'
              onChange={this.updateField.bind(this, 'staffUsername')}
              value={this.state.staffUsername}
              isValid={isValid}
              message={message}
              required
            />
          )}
        />
        <Validator rules={rules}
          fieldName='staffPassword' value={this.state.staffPassword} render={({ isValid, message }) => (
            <FieldGroup
              id='staffPassword'
              type='text'
              label='Staff Password'
              onChange={this.updateField.bind(this, 'staffPassword')}
              value={this.state.staffPassword}
              isValid={isValid}
              message={message}
              required
            />
          )}
        />
        <Validator rules={rules}
          fieldName='logonWorkstationId' value={this.state.logonWorkstationId} render={({ isValid, message }) => (
            <FieldGroup
              id='logonWorkstationId'
              type='text'
              label='Logon WorkstationId'
              onChange={this.updateField.bind(this, 'logonWorkstationId')}
              value={this.state.logonWorkstationId}
              isValid={isValid}
              message={message}
              required
            />
          )}
        />
        <FormGroup>
          <Checkbox checked={this.state.notificationHandling} onChange={this.updateCheckbox.bind(this, 'notificationHandling')} inline>Handle Notifications?</Checkbox>       
        </FormGroup>
        <Validator rules={rules}
          fieldName='bounceHandlingType' value={this.state.bounceHandlingType} render={({ isValid, message }) => (
            <FieldGroup
              id='bounceHandlingType'
              type='select'
              label='Bounce Handling Type'
              onChange={this.updateField.bind(this, 'bounceHandlingType')}
              value={this.state.bounceHandlingType}
              isValid={isValid}
              message={message}
              required
            >
              <option value='None'>None</option>
              <option value='Email'>Email</option>
              <option value='Account'>Account</option>
              <option value='AddToListOnly'>Add to List Only</option>
            </FieldGroup>
          )}
        />
        <FieldGroup
          id='bounceListId'
          type='text'
          label='Bounce List Id'
          onChange={this.updateField.bind(this, 'bounceListId')}
          value={this.state.bounceListId}
        />
        <FieldGroup
          id='spamListId'
          type='text'
          label='Spam List Id'
          onChange={this.updateField.bind(this, 'spamListId')}
          value={this.state.spamListId}
        />
        <Validator rules={rules}
          fieldName='noteHandlingType' value={this.state.noteHandlingType} render={({ isValid, message }) => (
            <FieldGroup
              id='noteHandlingType'
              type='select'
              label='Note Handling Type'
              onChange={this.updateField.bind(this, 'noteHandlingType')}
              value={this.state.noteHandlingType}
              isValid={isValid}
              message={message}
              required
            >
              <option value='None'>None</option>
              <option value='Blocking'>Blocking</option>
              <option value='NonBlocking'>Non Blocking</option>
            </FieldGroup>
          )}
        />
        <Button bsStyle='primary' type="submit">{buttonText}</Button>
      </form>
    );
  }
}

export { PolarisConfigFields };